import React, { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import { Notification, toast } from "components/ui";
import useIdleTimer from "hooks/useIdleTimer";
import useSessionIdle from "hooks/useSessionIdle";
import SessionExpiredDialog from "services/pusher/SessionExpiredDialog";
import { signOutGlobally } from "store/auth/authStore";
import V2AuthService from "views/v2/services/AuthService";
import {
  IDLE_DIALOG_AUTO_LOGOUT_SECONDS,
  IDLE_DIALOG_MESSAGE,
  IDLE_DIALOG_TITLE,
} from "./constants";
import { authStore } from "./authStore";
import { bootstrapSession, performRefresh } from "./refreshSession";

/**
 * Restores session on mount. Path 1 refresh runs on API calls or user activity
 * when JWT is near expiry. Shows inactivity dialog before forced logout.
 */
const AuthProvider = ({ children }) => {
  const { isAuthenticated, usesRefreshCookie } = useSyncExternalStore(
    authStore.subscribe,
    authStore.getState,
    authStore.getState,
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [extending, setExtending] = useState(false);

  const closeDialog = useCallback(() => {
    setDialogOpen(false);
    setExtending(false);
    authStore.setIdleDialogOpen(false);
  }, []);

  const handleForceLogout = useCallback(() => {
    closeDialog();
    V2AuthService.signOut().catch(() => {});
    toast.push(
      <Notification title="Signed out" type="warning" closable>
        You were signed out due to inactivity.
      </Notification>,
    );
    signOutGlobally();
  }, [closeDialog]);

  const handleIdleWarning = useCallback(() => {
    authStore.setIdleDialogOpen(true);
    setDialogOpen(true);
  }, []);

  // If session was cleared (e.g. redirect to /sign-in), never leave the popup up.
  useEffect(() => {
    if (!isAuthenticated && dialogOpen) {
      closeDialog();
    }
  }, [isAuthenticated, dialogOpen, closeDialog]);

  const { resetIdle } = useSessionIdle({
    enabled: isAuthenticated && usesRefreshCookie && !dialogOpen,
    onIdleWarning: handleIdleWarning,
  });

  useEffect(() => {
    bootstrapSession();
  }, []);

  useIdleTimer(isAuthenticated && usesRefreshCookie && !dialogOpen);

  const handleLogOut = useCallback(() => {
    handleForceLogout();
  }, [handleForceLogout]);

  const handleStaySignedIn = useCallback(async () => {
    if (extending) {
      return;
    }

    setExtending(true);

    try {
      await performRefresh();
      closeDialog();
      authStore.recordActivity();
      resetIdle();
      toast.push(
        <Notification title="Session updated" type="success" closable>
          You are still signed in.
        </Notification>,
      );
    } catch {
      closeDialog();
      toast.push(
        <Notification title="Session expired" type="danger" closable>
          Your session is no longer valid. Please log in again.
        </Notification>,
      );
      signOutGlobally();
    }
  }, [closeDialog, extending, resetIdle]);

  return (
    <>
      {children}
      <SessionExpiredDialog
        isOpen={isAuthenticated && dialogOpen}
        loading={extending}
        title={IDLE_DIALOG_TITLE}
        message={IDLE_DIALOG_MESSAGE}
        autoLogoutSeconds={IDLE_DIALOG_AUTO_LOGOUT_SECONDS}
        onStay={handleStaySignedIn}
        onLogOut={handleLogOut}
      />
    </>
  );
};

export default AuthProvider;
